import { useState } from 'react'
import { Button, CloseButton, Dialog, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { FiAlertTriangle } from 'react-icons/fi'
import { ResponsiveModal } from '../shared'
import type { DebriefAskOptionDto } from '../../types/lobbyDay'

interface AttachAskControlProps {
  meetingId: string
  askId: string | null
  askTitle: string | null
  askOptions: DebriefAskOptionDto[]
  onAttach: (askId: string | null) => void
}

// LW-21: a no-ask meeting is a debrief gap the organizer needs to close before the
// ask-outcome tallies mean anything -- echo LW-3's schedule-side "⚠ No ask" treatment
// (MeetingAgendaCard, warning.fg) with a warning-toned button that opens a picker of the
// event's existing asks. There is no create-new-ask affordance here -- that lives on the
// Asks tab; the picker only ever lists askOptions.
export function AttachAskControl({ askId, askTitle, askOptions, onAttach }: AttachAskControlProps) {
  const [open, setOpen] = useState(false)

  const pick = (id: string) => {
    setOpen(false)
    onAttach(id)
  }

  return (
    <>
      {askId ? (
        <HStack gap={2} mt={1} justify="space-between">
          <Text fontSize="sm">{askTitle}</Text>
          <HStack gap={1}>
            <Button size="xs" variant="ghost" minH="44px" onClick={() => setOpen(true)}>
              Change
            </Button>
            <Button size="xs" variant="ghost" colorPalette="red" minH="44px" onClick={() => onAttach(null)}>
              Clear
            </Button>
          </HStack>
        </HStack>
      ) : (
        <Button
          size="sm"
          variant="outline"
          colorPalette="orange"
          color="warning.fg"
          minH="44px"
          mt={1}
          onClick={() => setOpen(true)}
        >
          <Icon as={FiAlertTriangle} />
          No ask — attach one
        </Button>
      )}

      <ResponsiveModal isOpen={open} onClose={() => setOpen(false)} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Attach an ask</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack align="stretch" gap={2} pb={2}>
                {askOptions.length === 0 && (
                  <Text fontSize="sm" color="muted">This event has no asks yet.</Text>
                )}
                {askOptions.map((o) => (
                  <Button
                    key={o.askId}
                    variant="outline"
                    minH="44px"
                    justifyContent="flex-start"
                    onClick={() => pick(o.askId)}
                  >
                    {o.title}
                  </Button>
                ))}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </>
  )
}
